import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser, languageKeys } from '../../util/data';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import config from '../../config';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  Modal,
  Avatar,
  AvatarLarge,
  ImageFromFile,
  IconSpinner,
  Button,
  ProfileSettingsTile,
  ProfileSettingsModal,
  CodeOfConductModalContent,
  ModalCompleteProfile,
  ToolLogo,
} from '../../components';
import {
  SignupProviderLocationNLanguageForm,
  SignupProviderSkillsForm,
  ProfileSettingsSkillsForm,
  ProfileSettingsToolsForm,
  ProfileSettingsHobbiesForm,
  SignupProviderCOC,
  ProfileSettingsNameLocationWorkingMethodForm,
  ProfileSettingsBioWhoAmIForm,
  ProfileSettingsHourlyFeeIdealCustomerForm,
  RequestWorkExperienceTestimonialForm,
  ProfileSettingsBasicInfoForm,
  ProfileSettingsProfileTitleForm,
  ProfileSettingsProfileDescriptionForm,
  ProfileSettingsEducationForm,
  ProfileSettingsPastClientForm,
  ProfileSettingsWorkExperienceForm,
} from '../../forms';
import { TopbarContainer } from '../../containers';
import {
  updateProfile,
  uploadImage,
  clearImage,
  setInitialState,
  requestTestimonial,
  queryOffPlatformTestimonials,
  loadData,
} from './ProfileSettingsPage.duck';
import css from './ProfileSettingsPage.css';

import { TbPencil } from 'react-icons/tb';
import { AiOutlinePlus } from 'react-icons/ai';
import { MdDragIndicator } from 'react-icons/md';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { MdLocationOn, MdLanguage, MdCheckCircle } from 'react-icons/md';
import { BiEuro } from 'react-icons/bi';
import { IoIosArrowForward } from 'react-icons/io';
import { MdVerified } from 'react-icons/md';

import ScorePill from '../../components/ProfileSettingsTile/ScorePill';

import { withViewport } from '../../util/contextHelpers';
import { createSlug } from '../../util/urlHelpers';
import { v4 as uuidv4 } from 'uuid';
import { isUploadImageOverLimitError } from '../../util/errors';

// Bio truncation helpers
const BIO_PREVIEW_LENGTH = 600;
const HTML_ENTITY_MAX_LENGTH = 10;

const getSafeBioSlice = (text, maxLength) => {
  if (text.length <= maxLength) return text;

  const textOnly = text.replace(/<[^>]*>/g, '');
  if (textOnly.length <= maxLength) return text;

  let sliced = text.slice(0, maxLength);

  const lastAmpersand = sliced.lastIndexOf('&');
  const lastSemicolon = sliced.lastIndexOf(';');
  if (lastAmpersand > lastSemicolon) {
    const nextSemicolon = text.indexOf(';', maxLength);
    if (nextSemicolon !== -1 && nextSemicolon - lastAmpersand < HTML_ENTITY_MAX_LENGTH) {
      sliced = text.slice(0, lastAmpersand);
    }
  }

  const lastOpenTag = sliced.lastIndexOf('<');
  const lastCloseTag = sliced.lastIndexOf('>');
  if (lastOpenTag > lastCloseTag) {
    sliced = sliced.slice(0, lastOpenTag);
  }

  const lastSpace = sliced.lastIndexOf(' ');
  if (lastSpace > sliced.length * 0.8) {
    sliced = sliced.slice(0, lastSpace);
  }

  return sliced;
};

const categoriesOptions = config.custom.categoriesOptions;
const skillLevelOptions = config.custom.skillLevelOptions;
const languageOptions = config.custom.filters[3].config.options;
const hobbyOptions = config.custom.hobbyOptions;

// ================ Portfolio reorder helpers ================ //

const getPortfolioImageUrl = work => {
  const firstImage = work?.images?.[0];
  return (
    firstImage?.attributes?.variants?.['landscape-crop2x']?.url ||
    firstImage?.attributes?.variants?.['landscape-crop']?.url ||
    firstImage?.attributes?.variants?.default?.url ||
    null
  );
};

const SortablePortfolioCard = ({ id, work }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: transform
      ? `translate3d(${Math.round(transform.x || 0)}px, ${Math.round(
          transform.y || 0
        )}px, 0) scaleX(${transform.scaleX ?? 1}) scaleY(${transform.scaleY ?? 1})`
      : undefined,
    transition,
    zIndex: isDragging ? 2 : 1,
  };
  const imageUrl = getPortfolioImageUrl(work);
  const title = work.attributes.title;
  const classes = [css.portfolioCard, css.portfolioCardDraggable];
  if (isDragging) classes.push(css.portfolioCardDragging);
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classes.join(' ')}
      {...attributes}
      {...listeners}
    >
      {imageUrl ? <img src={imageUrl} alt={title} className={css.portfolioImage} /> : null}
      <div className={css.portfolioOverlay}>
        <p className={css.portfolioTitle}>{title}</p>
      </div>
      <div className={css.dragHandleHint} aria-hidden="true">
        <MdDragIndicator />
      </div>
    </div>
  );
};

// ================ Main Component ================ //

const ProfileSettingsPageComponent = props => {
  const {
    currentUser,
    image,
    onImageUpload,
    onClearImage,
    onUpdateProfile,
    onRequestTestimonial,
    onQueryOffPlatformTestimonials,
    offPlatformTestimonials,
    onManageDisableScrolling,
    scrollingDisabled,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
    skillOptions,
    toolOptions,
    skillCategoryOptions,
    listings,
    intl,
    onSetInitialState,
    viewport,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const { firstName, lastName, bio, protectedData, publicData } = user.attributes.profile;

  // Modal state (legacy pattern for old modals)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  // New modal state (string key pattern)
  const [activeModal, setActiveModal] = useState(null);
  const openModal = modalKey => () => setActiveModal(modalKey);
  const closeActiveModal = () => setActiveModal(null);

  // Past client modal state
  const [editingPastClient, setEditingPastClient] = useState(null);

  // Work experience modal state
  const [editingWorkExperience, setEditingWorkExperience] = useState(null);

  // Request-testimonial modal state — preselection is { id, source }
  const [testimonialRequest, setTestimonialRequest] = useState(null);

  // Education modal state
  const [editingEducation, setEditingEducation] = useState(null);

  // Image upload state
  const [imageToBeDeleted, setImageToBeDeleted] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);
  const [showAllPortfolio, setShowAllPortfolio] = useState(false);
  const [expandedTestimonials, setExpandedTestimonials] = useState({});

  // Portfolio reorder state
  const [isPortfolioReorderMode, setIsPortfolioReorderMode] = useState(false);
  const [portfolioReorderIds, setPortfolioReorderIds] = useState([]);

  const portfolioWorks = useMemo(
    () =>
      listings.filter(
        l =>
          l?.attributes?.publicData?.listingType === 'portfolioWork' &&
          l?.attributes?.state !== 'closed'
      ),
    [listings]
  );

  const featuredPortfolioOrder = publicData?.featuredPortfolioOrder || [];

  const sortedPortfolioWorks = useMemo(() => {
    const orderMap = new Map(featuredPortfolioOrder.map(e => [e.id, e.order]));
    return [...portfolioWorks].sort((a, b) => {
      const oa = orderMap.get(a.id.uuid);
      const ob = orderMap.get(b.id.uuid);
      if (oa != null && ob != null) return oa - ob;
      if (oa != null) return -1;
      if (ob != null) return 1;
      return 0;
    });
  }, [portfolioWorks, featuredPortfolioOrder]);

  const portfolioWorksById = useMemo(() => {
    const map = {};
    portfolioWorks.forEach(w => {
      map[w.id.uuid] = w;
    });
    return map;
  }, [portfolioWorks]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleEnterPortfolioReorder = () => {
    setPortfolioReorderIds(sortedPortfolioWorks.map(w => w.id.uuid));
    setIsPortfolioReorderMode(true);
  };

  const handleCancelPortfolioReorder = () => {
    setIsPortfolioReorderMode(false);
  };

  const handleTogglePortfolioReorder = () => {
    if (isPortfolioReorderMode) {
      handleCancelPortfolioReorder();
    } else {
      handleEnterPortfolioReorder();
    }
  };

  const handleSavePortfolioReorder = () => {
    const newOrder = portfolioReorderIds.map((id, idx) => ({ id, order: idx + 1 }));
    const result = onUpdateProfile({
      publicData: {
        ...publicData,
        featuredPortfolioOrder: newOrder,
      },
    });
    Promise.resolve(result)
      .then(() => setIsPortfolioReorderMode(false))
      .catch(() => {
        /* keep user in reorder mode; error surfaced via updateProfileError */
      });
  };

  const handlePortfolioDragEnd = event => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPortfolioReorderIds(items => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const profileImageId = user.profileImage ? user.profileImage.id : null;
  const profileImage = image || { imageId: profileImageId };

  // ================ Initial values (kept from original) ================ //

  let initialValues = {};
  initialValues = {
    ...initialValues,
    firstName: firstName || '',
    lastName: lastName || '',
    bio: bio || '',
    vat_number: protectedData?.vat_number || '',
    phoneNumber: protectedData?.phoneNumber || '',
    website: protectedData?.website || '',
    linkedIn: protectedData?.linkedIn || '',
    companyName: publicData?.companyName || '',
    industry: protectedData?.industry || '',
    address: publicData?.address || '',
    zipCode: publicData?.zipCode || '',
    city: publicData?.city || '',
    country: publicData?.country || '',
    noIndexing: publicData?.noIndexing || false,
    categories: publicData?.categories?.length
      ? publicData.categories
          .map(each => {
            return typeof each === 'string' ? categoriesOptions.find(e => e.key === each) : each;
          })
          .filter(each => each !== undefined)
      : [],
    profileImage: user.profileImage,
    experience: publicData?.experience?.length
      ? publicData.experience.map(each => ({
          category: skillOptions.find(e => e.key === each.category),
          level: each.level
            ? {
                key: each.level,
                value: each.level,
                label: intl.formatMessage({
                  id: skillLevelOptions.find(e => e.key === each.level)?.label,
                }),
              }
            : '',
          isMainSkill: each.isMainSkill,
        }))
      : [],
    tools: publicData?.tools?.length
      ? publicData.tools.map(each => ({
          category: toolOptions.find(e => e.key === each.category),
        }))
      : [],
    hobbies: publicData?.hobbies?.length
      ? publicData.hobbies.map(each => ({
          category: hobbyOptions.find(e => e.key === each.category),
        }))
      : [],
    workExperience: publicData?.workExperience?.length
      ? publicData?.workExperience?.map(e => ({
          ...e,
          startYear: { key: e?.startYear, label: e?.startYear, value: e?.startYear },
          startMonth: { key: e?.startMonth, label: e?.startMonth, value: e?.startMonth },
          endYear: { key: e?.endYear, label: e?.endYear, value: e?.endYear },
          endMonth: { key: e?.endMonth, label: e?.endMonth, value: e?.endMonth },
        }))
      : [],
    workedWith: publicData?.workedWith?.length
      ? publicData?.workedWith?.map(e => ({
          ...e,
          startYear: { key: e?.startYear, label: e?.startYear, value: e?.startYear },
          startMonth: { key: e?.startMonth, label: e?.startMonth, value: e?.startMonth },
          endYear: { key: e?.endYear, label: e?.endYear, value: e?.endYear },
          endMonth: { key: e?.endMonth, label: e?.endMonth, value: e?.endMonth },
        }))
      : [],
    education: publicData?.education?.length
      ? publicData?.education?.map(e => ({
          ...e,
          when: { key: e?.when, label: e?.when, value: e?.when },
        }))
      : [],
    languages: publicData?.languages?.length
      ? publicData?.languages?.map(key => ({
          language: languageOptions.find(e => e.key === key),
        }))
      : [],
    hourlyFee: publicData?.hourlyFee,
    dontShowHourlyFee: publicData?.dontShowHourlyFee,
    idealCustomer: publicData?.idealCustomer,
    whoAmI: publicData?.whoAmI,
    workingMethod: publicData?.workingMethod,
    location: publicData?.location,
    cocAccepted: publicData?.cocAccepted,
  };

  // ================ Handlers ================ //

  const handleSubmit = values => {
    const {
      firstName,
      lastName,
      bio: rawBio,
      vat_number,
      phoneNumber,
      industry,
      companyName,
      address,
      zipCode,
      city,
      country,
      categories,
      experience,
      tools,
      hobbies,
      workExperience,
      dontAddWorkExperience,
      workedWith,
      dontAddWorkedWith,
      education,
      dontAddEducation,
      languages,
      hourlyFee,
      dontShowHourlyFee,
      idealCustomer,
      whoAmI,
      workingMethod,
      location,
      website,
      linkedIn,
      cocAccepted,
      noIndexing,
    } = values;

    if (!values.workExperience) {
      values.workExperience = [];
    }

    const bio = rawBio || '';

    const notificationLang =
      Array.isArray(languages) &&
      languages.some(lang => lang.language?.key === 'finnish' || lang === 'finnish')
        ? 'finnish'
        : 'english';

    const profile = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      bio,
      protectedData: {
        vat_number,
        phoneNumber,
        industry,
        website,
        linkedIn,
      },
      privateData: {
        notificationLanguage: notificationLang,
      },
      publicData: {
        categories: categories?.map(e => e.key) || [],
        companyName,
        address,
        zipCode,
        city,
        country,
        bio,
        experience:
          experience?.map(e => ({
            category: e?.category?.key,
            level: e?.level?.key,
            isMainSkill: e?.isMainSkill ? e?.isMainSkill : false,
          })) || [],
        experienceKeys: experience?.map(e => e?.category?.key) || [],
        tools:
          tools?.map(e => ({
            category: e?.category?.key,
          })) || [],
        toolsKeys: tools?.map(e => e?.category?.key) || [],
        hobbies:
          hobbies?.map(e => ({
            category: e?.category?.key,
          })) || [],
        workExperience:
          workExperience?.length !== 0 && !dontAddWorkExperience
            ? workExperience?.map(e => {
                return {
                  ...e,
                  startYear: e.startYear?.key,
                  endYear: e.endYear?.key,
                  startMonth: e.startMonth?.key,
                  endMonth: e.endMonth?.key,
                  id: e.id || uuidv4(),
                };
              })
            : [],
        workedWith:
          workedWith?.length > 0 && !dontAddWorkedWith
            ? workedWith?.map(e => {
                return {
                  ...e,
                  startYear: e?.startYear?.key,
                  endYear: e?.endYear?.key,
                  startMonth: e?.startMonth?.key,
                  endMonth: e?.endMonth?.key,
                  id: e.id || uuidv4(),
                };
              })
            : [],
        education:
          education?.length > 0 && !dontAddEducation
            ? education?.map(e => {
                return { ...e, when: e?.when?.key };
              })
            : [],
        languages: languageKeys(languages),
        hourlyFee: hourlyFee !== undefined ? hourlyFee : [],
        dontShowHourlyFee: dontShowHourlyFee !== undefined ? dontShowHourlyFee : [],
        idealCustomer: idealCustomer !== undefined ? idealCustomer : [],
        whoAmI: whoAmI !== undefined ? whoAmI : '',
        workingMethod: workingMethod !== undefined ? workingMethod : '',
        location: location !== undefined ? location : '',
        locationCity: location?.selectedPlace?.city || location?.city || '',
        locationCountry: location?.selectedPlace?.country || location?.country || '',
        locationCountryCode: location?.selectedPlace?.countryCode || location?.countryCode || '',
        cocAccepted: cocAccepted !== undefined ? cocAccepted : false,
        noIndexing: noIndexing || false,
      },
    };
    const uploadedImage = props.image;

    const updatedValues =
      (uploadedImage && uploadedImage.imageId && uploadedImage.file) || imageToBeDeleted
        ? {
            ...profile,
            profileImageId: imageToBeDeleted ? null : uploadedImage.imageId,
          }
        : profile;

    onUpdateProfile(updatedValues);
    setImageToBeDeleted(false);
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleButtonClick = content => () => {
    setModalContent(content);
    handleOpenModal();
  };

  const handleSubmitClick = values => {
    handleSubmit(values);
    handleCloseModal();
  };

  // Partial submit - only sends the provided fields to the API
  const handlePartialSubmit = partialValues => {
    const profile = {};
    const publicDataUpdate = {};

    if (partialValues.firstName !== undefined) {
      profile.firstName = partialValues.firstName;
    }
    if (partialValues.lastName !== undefined) {
      profile.lastName = partialValues.lastName;
    }
    if (partialValues.firstName !== undefined || partialValues.lastName !== undefined) {
      const uploadedId = profileImage?.uploadedImage?.id;
      if (uploadedId) {
        profile.profileImageId = uploadedId;
      }
    }
    if (partialValues.whoAmI !== undefined) {
      publicDataUpdate.whoAmI = partialValues.whoAmI;
    }
    if (partialValues.location !== undefined) {
      publicDataUpdate.location = partialValues.location;
      publicDataUpdate.locationCity =
        partialValues.location?.selectedPlace?.city || partialValues.location?.city || '';
      publicDataUpdate.locationCountry =
        partialValues.location?.selectedPlace?.country || partialValues.location?.country || '';
      publicDataUpdate.locationCountryCode =
        partialValues.location?.selectedPlace?.countryCode ||
        partialValues.location?.countryCode ||
        '';
    }
    if (partialValues.languages !== undefined) {
      const langs = languageKeys(partialValues.languages);
      publicDataUpdate.languages = langs;
      const isFinnish = langs.includes('finnish');
      const existingPrivateData = user.attributes?.profile?.privateData || {};
      profile.privateData = {
        ...existingPrivateData,
        notificationLanguage: isFinnish ? 'finnish' : 'english',
      };
    }
    if (partialValues.hourlyFee !== undefined) {
      publicDataUpdate.hourlyFee = partialValues.hourlyFee;
    }
    if (partialValues.dontShowHourlyFee !== undefined) {
      publicDataUpdate.dontShowHourlyFee = partialValues.dontShowHourlyFee;
    }
    if (partialValues.bio !== undefined) {
      profile.bio = partialValues.bio;
      publicDataUpdate.bio = partialValues.bio;
    }
    if (partialValues.workedWith !== undefined) {
      publicDataUpdate.workedWith = partialValues.workedWith;
    }
    if (partialValues.privateWorkedWith !== undefined) {
      publicDataUpdate.privateWorkedWith = partialValues.privateWorkedWith;
    }
    if (partialValues.workExperience !== undefined) {
      publicDataUpdate.workExperience = partialValues.workExperience;
    }
    if (partialValues.privateWorkExperience !== undefined) {
      publicDataUpdate.privateWorkExperience = partialValues.privateWorkExperience;
    }
    if (partialValues.education !== undefined) {
      publicDataUpdate.education = partialValues.education;
    }
    if (partialValues.tools !== undefined) {
      publicDataUpdate.tools = partialValues.tools;
      publicDataUpdate.toolsKeys =
        partialValues.toolsKeys || partialValues.tools.map(t => t.category);
    }
    if (partialValues.experienceKeys !== undefined) {
      publicDataUpdate.experienceKeys = partialValues.experienceKeys;
      // Clear the legacy {category, level, isMainSkill} array on every save so
      // listings and components only need to consider the new flat shape.
      publicDataUpdate.experience = null;
    }
    if (partialValues.hobbies !== undefined) {
      publicDataUpdate.hobbies = partialValues.hobbies;
    }
    if (partialValues.cocAccepted !== undefined) {
      publicDataUpdate.cocAccepted = partialValues.cocAccepted;
    }

    if (Object.keys(publicDataUpdate).length > 0) {
      profile.publicData = publicDataUpdate;
    }

    onUpdateProfile(profile);
  };

  // One-click add from the skills search: saves the tool straight away
  const handleAddToolFromSkills = toolKey => {
    const toolsKeys = (publicData?.tools || []).map(t => t.category);
    if (toolsKeys.includes(toolKey)) return;
    const nextToolsKeys = [...toolsKeys, toolKey];
    handlePartialSubmit({
      tools: nextToolsKeys.map(key => ({ category: key })),
      toolsKeys: nextToolsKeys,
    });
  };

  const closeRequestTestimonialModal = () => {
    closeActiveModal();
    setTestimonialRequest(null);
  };

  // Past client handlers
  const openAddPastClient = () => {
    setEditingPastClient({ item: null, isPublic: true });
    setActiveModal('pastClient');
  };

  const openEditPastClient = (item, isPublic) => () => {
    setEditingPastClient({ item, isPublic });
    setActiveModal('pastClient');
  };

  const closePastClientModal = () => {
    closeActiveModal();
    setEditingPastClient(null);
  };

  const handlePastClientSubmit = ({ item, isPublic }) => {
    const currentPublic = [...(publicData?.workedWith || [])];
    const currentPrivate = [...(publicData?.privateWorkedWith || [])];

    const flatItem = {
      ...item,
      startYear: item.startYear?.key ?? item.startYear,
      startMonth: item.startMonth?.key ?? item.startMonth,
      endYear: item.endYear?.key ?? item.endYear,
      endMonth: item.endMonth?.key ?? item.endMonth,
      taskType: Array.isArray(item.taskType) ? item.taskType : [item.taskType],
      id: item.id || editingPastClient?.item?.id || uuidv4(),
    };

    // Remove from old location if editing existing
    const existingId = editingPastClient?.item?.id;
    if (existingId) {
      const idxPub = currentPublic.findIndex(c => c.id === existingId);
      if (idxPub !== -1) currentPublic.splice(idxPub, 1);
      const idxPriv = currentPrivate.findIndex(c => c.id === existingId);
      if (idxPriv !== -1) currentPrivate.splice(idxPriv, 1);
    }

    // Add to correct array
    if (isPublic) {
      currentPublic.push(flatItem);
    } else {
      currentPrivate.push(flatItem);
    }

    handlePartialSubmit({ workedWith: currentPublic, privateWorkedWith: currentPrivate });
    closePastClientModal();
  };

  const handlePastClientDelete = itemId => {
    const currentPublic = (publicData?.workedWith || []).filter(c => c.id !== itemId);
    const currentPrivate = (publicData?.privateWorkedWith || []).filter(c => c.id !== itemId);
    handlePartialSubmit({ workedWith: currentPublic, privateWorkedWith: currentPrivate });
  };

  const handleOpenRequestTestimonialFromPastClient = item => {
    setTestimonialRequest({ id: item.id, source: 'workedWith' });
    setActiveModal('requestTestimonial');
  };

  // Work experience handlers
  const openAddWorkExperience = () => {
    setEditingWorkExperience({ item: null, isPublic: true });
    setActiveModal('workExperience');
  };

  const openEditWorkExperience = (item, isPublic) => () => {
    setEditingWorkExperience({ item, isPublic });
    setActiveModal('workExperience');
  };

  const closeWorkExperienceModal = () => {
    closeActiveModal();
    setEditingWorkExperience(null);
  };

  const handleWorkExperienceSubmit = ({ item, isPublic }) => {
    const currentPublic = [...(publicData?.workExperience || [])];
    const currentPrivate = [...(publicData?.privateWorkExperience || [])];

    const flatItem = {
      ...item,
      startYear: item.startYear?.key ?? item.startYear,
      startMonth: item.startMonth?.key ?? item.startMonth,
      endYear: item.endYear?.key ?? item.endYear,
      endMonth: item.endMonth?.key ?? item.endMonth,
      taskType: Array.isArray(item.taskType) ? item.taskType : [item.taskType],
      id: item.id || editingWorkExperience?.item?.id || uuidv4(),
    };

    // Remove from old location if editing existing
    const existingId = editingWorkExperience?.item?.id;
    if (existingId) {
      const idxPub = currentPublic.findIndex(c => c.id === existingId);
      if (idxPub !== -1) currentPublic.splice(idxPub, 1);
      const idxPriv = currentPrivate.findIndex(c => c.id === existingId);
      if (idxPriv !== -1) currentPrivate.splice(idxPriv, 1);
    }

    // Add to correct array
    if (isPublic) {
      currentPublic.push(flatItem);
    } else {
      currentPrivate.push(flatItem);
    }

    handlePartialSubmit({ workExperience: currentPublic, privateWorkExperience: currentPrivate });
    closeWorkExperienceModal();
  };

  const handleWorkExperienceDelete = itemId => {
    const currentPublic = (publicData?.workExperience || []).filter(c => c.id !== itemId);
    const currentPrivate = (publicData?.privateWorkExperience || []).filter(c => c.id !== itemId);
    handlePartialSubmit({ workExperience: currentPublic, privateWorkExperience: currentPrivate });
  };

  const handleOpenRequestTestimonialFromWorkExperience = item => {
    setTestimonialRequest({ id: item.id, source: 'workExperience' });
    setActiveModal('requestTestimonial');
  };

  // Education handlers
  const openAddEducation = () => {
    setEditingEducation({ item: null });
    setActiveModal('education');
  };

  const openEditEducation = item => () => {
    setEditingEducation({ item });
    setActiveModal('education');
  };

  const closeEducationModal = () => {
    closeActiveModal();
    setEditingEducation(null);
  };

  const handleEducationSubmit = ({ item }) => {
    const current = [...(publicData?.education || [])];

    const flatItem = {
      where: item.where,
      degree: item.degree,
      industry: item.industry,
      when: item.when?.key ?? item.when,
      id: item.id || editingEducation?.item?.id || uuidv4(),
    };

    // Remove from old location if editing existing
    const existingId = editingEducation?.item?.id;
    if (existingId) {
      const idx = current.findIndex(c => c.id === existingId);
      if (idx !== -1) current.splice(idx, 1);
    }

    current.push(flatItem);
    handlePartialSubmit({ education: current });
    closeEducationModal();
  };

  const handleEducationDelete = itemId => {
    const current = (publicData?.education || []).filter(c => c.id !== itemId);
    handlePartialSubmit({ education: current });
  };

  // ================ Role checks ================ //

  const isValidCustomer =
    currentUser?.attributes?.profile?.publicData?.account_role === 'customer' &&
    !currentUser?.attributes?.banned &&
    !currentUser?.attributes?.deleted;

  const isValidProvider =
    (currentUser?.attributes?.profile?.publicData?.account_role === 'freelancer' &&
      !currentUser?.attributes?.banned &&
      !currentUser?.attributes?.deleted) ||
    (currentUser?.attributes?.profile?.publicData?.account_role === 'light-entrepreneur' &&
      !currentUser?.attributes?.banned &&
      !currentUser?.attributes?.deleted);

  const showContainers = !isValidCustomer && isValidProvider;

  // ================ Modal content definitions ================ //

  const nameLocationWorkingMethodModalContent = (
    <ProfileSettingsNameLocationWorkingMethodForm
      initialValues={initialValues}
      onSubmit={handleSubmitClick}
      isModal={true}
      user={user}
      isValidCustomer={isValidCustomer}
      isValidProvider={isValidProvider}
    />
  );

  const bioWhoAmImodalContent = (
    <ProfileSettingsBioWhoAmIForm
      initialValues={initialValues}
      onSubmit={handleSubmitClick}
      isModal={true}
      isValidProvider={isValidProvider}
    />
  );

  const codeOfConductModalContent = (
    <SignupProviderCOC initialValues={initialValues} onSubmit={handleSubmitClick} isModal={true} />
  );

  const hourlyFeeIdealCustomerModalContent = (
    <ProfileSettingsHourlyFeeIdealCustomerForm
      initialValues={initialValues}
      onSubmit={handleSubmitClick}
      isModal={true}
    />
  );

  const initialExperienceKeys =
    publicData?.experienceKeys && publicData.experienceKeys.length
      ? publicData.experienceKeys
      : (publicData?.experience || []).map(e => e?.category).filter(Boolean);

  const languagesModalContent = (
    <SignupProviderLocationNLanguageForm
      initialValues={initialValues}
      onSubmit={handleSubmitClick}
      isModal={true}
    />
  );

  // ================ Data checks for empty/filled states ================ //

  const hasBio = !!bio && bio !== '<p><br></p>';
  const hasExperience = initialExperienceKeys.length > 0;
  const hasTools = Array.isArray(publicData?.tools) && publicData.tools.length > 0;
  const hasHobbies = Array.isArray(publicData?.hobbies) && publicData.hobbies.length > 0;
  const hasPortfolio = portfolioWorks.length > 0;
  const hasTestimonials =
    Array.isArray(offPlatformTestimonials) && offPlatformTestimonials.length > 0;
  const hasWorkedWith = Array.isArray(publicData?.workedWith) && publicData.workedWith.length > 0;
  const hasPrivateWorkedWith =
    Array.isArray(publicData?.privateWorkedWith) && publicData.privateWorkedWith.length > 0;
  const hasWorkExperience =
    Array.isArray(publicData?.workExperience) && publicData.workExperience.length > 0;
  const hasPrivateWorkExperience =
    Array.isArray(publicData?.privateWorkExperience) && publicData.privateWorkExperience.length > 0;
  const hasEducation = Array.isArray(publicData?.education) && publicData.education.length > 0;
  const hasLocation = !!publicData?.location;
  const hasLanguages = Array.isArray(publicData?.languages) && publicData.languages.length > 0;
  const hasHourlyFee = !!publicData?.hourlyFee;
  const cocAccepted = !!publicData?.cocAccepted;
  const whoAmI = publicData?.whoAmI || '';

  // Location display text
  const locationCity =
    publicData?.location?.city || publicData?.location?.selectedPlace?.city || '';
  // Languages display - languageOptions labels are JSX elements, so capitalize the key instead
  const languagesDisplay = hasLanguages
    ? publicData.languages
        .map(l => {
          const key = typeof l === 'string' ? l : l.language;
          return key ? key.charAt(0).toUpperCase() + key.slice(1) : '';
        })
        .filter(Boolean)
        .join(', ')
    : '';
  // Hourly fee display
  const hourlyFeeDisplay = hasHourlyFee ? publicData.hourlyFee : '';

  // ================ Avatar rendering ================ //

  const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
  const transientUser = { ...user, profileImage: transientUserProfileImage };
  const fileExists = !!profileImage.file;
  const fileUploadInProgress = uploadInProgress && fileExists;

  const uploadingOverlay = fileUploadInProgress ? (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.7)',
        borderRadius: '100px',
      }}
    >
      <IconSpinner />
    </div>
  ) : null;

  // Render exactly one of: the local-file preview (while uploading),
  // the server-backed Avatar (once we have an imageId), or nothing.
  let avatarNode = null;
  if (fileUploadInProgress) {
    avatarNode = (
      <ImageFromFile
        id={profileImage.id}
        rootClassName={css.avatar}
        aspectRatioClassName={css.avatar}
        file={profileImage.file}
      >
        {uploadingOverlay}
      </ImageFromFile>
    );
  } else if (profileImage.imageId) {
    avatarNode = (
      <Avatar
        rootClassName={css.avatar}
        renderSizes="(max-width: 767px) 120px, 120px"
        user={transientUser}
        disableProfileLink
        disableVerifiedBadge
      />
    );
  }

  const hasProfileImage = !!avatarNode;

  // ================ Effects ================ //

  const title = intl.formatMessage({ id: 'ProfileSettingsPage.title' });

  useEffect(() => {
    onSetInitialState();
  }, [onSetInitialState]);

  useEffect(() => {
    if (currentUser?.id?.uuid) {
      onQueryOffPlatformTestimonials(currentUser?.id.uuid);
    }
  }, [onQueryOffPlatformTestimonials, currentUser]);

  // Hash navigation: scroll to the section and open the corresponding modal,
  // both for a deep link on mount and for in-page hash navigation (e.g.
  // clicking a tile in the complete-profile card). Each history entry is
  // handled once, so a currentUser update can't re-open a modal from a stale
  // hash.
  const location = useLocation();
  const handledHashLocationRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined' || !location.hash) return;
    if (!currentUser?.id) return;
    const locationKey = location.key || 'initial';
    if (handledHashLocationRef.current === locationKey) return;
    handledHashLocationRef.current = locationKey;

    const hash = location.hash.replace('#', '');

    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      switch (hash) {
        case 'bio':
          setActiveModal('profileDescription');
          break;
        case 'experience':
          setActiveModal('experience');
          break;
        case 'tools':
          setActiveModal('tools');
          break;
        case 'hobbies':
          setActiveModal('hobbies');
          break;
        case 'profileImage':
        case 'whoAmI':
          setActiveModal('profileHeader');
          break;
        case 'languages':
        case 'hourlyFee':
        case 'basicInformation':
          setActiveModal('basicInfo');
          break;
        case 'workExperience':
          setEditingWorkExperience({ item: null, isPublic: true });
          setActiveModal('workExperience');
          break;
        case 'workedWith':
          setEditingPastClient({ item: null, isPublic: true });
          setActiveModal('pastClient');
          break;
        case 'education':
          setEditingEducation({ item: null });
          setActiveModal('education');
          break;
        case 'testimonials':
          setActiveModal('requestTestimonial');
          break;
        case 'cocAccepted':
          setActiveModal('codeOfConduct');
          break;
        default:
          break;
      }
    }, 300);
  }, [currentUser, location]);

  // ================ Portfolio add link ================ //

  const portfolioAddLink = (
    <NamedLink name="NewPortfolioWorkPage" className={css.addButtonLink}>
      <span className={css.addButtonLinkText}>
        <FormattedMessage id="ProfileSettingsPage.addButton" />
      </span>
      <AiOutlinePlus className={css.addButtonLinkIcon} />
    </NamedLink>
  );

  // ================ Render ================ //

  const profileUser = ensureUser(user);

  return (
    <Page className={css.root} title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.mainContent}>
          <div className={css.content}>
            {/* Progress banner */}
            {/* Heading */}
            <div className={css.headingContainer}>
              <h1 className={css.heading}>
                <FormattedMessage id="ProfileSettingsPage.heading" />
              </h1>
              {user.id ? (
                <NamedLink
                  className={css.profileLink}
                  name="ProfilePage"
                  params={{ id: user.id.uuid }}
                >
                  <FormattedMessage id="ProfileSettingsPage.viewProfileLink" />
                </NamedLink>
              ) : null}
            </div>
            {showContainers ? (
              <ModalCompleteProfile showKeepUpdatedTile={false} compactWhenComplete />
            ) : null}
            {user.id ? (
              <div className={css.columnsContainer}>
                {/* ========== LEFT SIDEBAR ========== */}
                <div className={css.sidebar}>
                  {/* Profile Card */}
                  <div id="profileImage" className={css.profileCard}>
                    <button
                      type="button"
                      className={css.profileCardEditPen}
                      onClick={openModal('profileHeader')}
                      aria-label={intl.formatMessage({
                        id: 'ProfileSettingsPage.editProfileHeaderTitle',
                      })}
                    >
                      <TbPencil className={css.editPenIcon} />
                    </button>
                    <div className={css.avatarWrapper}>
                      {hasProfileImage ? (
                        avatarNode
                      ) : (
                        <AvatarLarge user={transientUser} disableProfileLink disableVerifiedBadge />
                      )}
                    </div>
                    <div className={css.profileNameSection}>
                      <p className={css.profileName}>
                        {firstName} {lastName || ''}
                      </p>
                      {whoAmI ? <span className={css.profileTitle}>{whoAmI}</span> : null}
                    </div>
                  </div>

                  {/* Basic Information */}
                  {showContainers || isValidCustomer ? (
                    <div id="basicInformation" className={css.sidebarCard}>
                      <div className={css.sidebarCardHeader}>
                        <h3 className={css.sidebarCardTitle}>
                          <FormattedMessage id="ProfileSettingsPage.basicInformation" />
                        </h3>
                        <button
                          type="button"
                          className={css.editPen}
                          onClick={openModal('basicInfo')}
                        >
                          <TbPencil className={css.editPenIcon} />
                        </button>
                      </div>
                      <div className={css.basicInfoList}>
                        {hasLocation && locationCity ? (
                          <div className={css.basicInfoItem}>
                            <MdLocationOn className={css.basicInfoIcon} />
                            <span className={css.basicInfoText}>{locationCity}</span>
                          </div>
                        ) : null}
                        {hasLanguages ? (
                          <div className={css.basicInfoItem}>
                            <MdLanguage className={css.basicInfoIcon} />
                            <span className={css.basicInfoText}>{languagesDisplay}</span>
                          </div>
                        ) : null}
                        {!isValidCustomer && hasHourlyFee ? (
                          <div className={css.basicInfoItem}>
                            <BiEuro className={css.basicInfoIcon} />
                            <span className={css.basicInfoText}>{hourlyFeeDisplay}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {/* Sopimukset / Agreements */}
                  {showContainers ? (
                    <div id="cocAccepted" className={css.agreementsCard}>
                      <h3 className={css.agreementsTitle}>
                        <FormattedMessage id="ProfileSettingsPage.agreements" />
                        {!cocAccepted ? <ScorePill points={5} /> : null}
                      </h3>
                      <div className={css.checklistItem}>
                        <div className={css.checklistTextRow}>
                          <MdCheckCircle
                            className={cocAccepted ? css.checkIconActive : css.checkIcon}
                          />
                          <span className={css.checklistText}>
                            <FormattedMessage id="ProfileSettingsPage.codeOfConduct" />
                          </span>
                        </div>
                        <div className={css.checklistActions}>
                          <button
                            type="button"
                            onClick={openModal('codeOfConduct')}
                            className={css.checklistLink}
                          >
                            <FormattedMessage id="ProfileSettingsPage.codeOfConductLink" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Upota profiilisi / Embed your profile */}
                  {showContainers ? (
                    <div className={css.embedCard}>
                      <h3 className={css.embedTitle}>
                        <FormattedMessage id="ProfileSettingsPage.embedCardTitle" />
                      </h3>
                      <p className={css.embedDescription}>
                        <FormattedMessage id="ProfileSettingsPage.embedCardDescription" />
                      </p>
                      <NamedLink name="EmbedWidgetPage" className={css.checklistLink}>
                        <FormattedMessage id="ProfileSettingsPage.embedCardButton" />
                      </NamedLink>
                    </div>
                  ) : null}
                </div>

                {/* ========== RIGHT CONTENT TILES ========== */}
                {showContainers ? (
                  <div className={css.tilesColumn}>
                    {/* About me */}
                    <ProfileSettingsTile
                      id="bio"
                      titleId="ProfileSettingsPage.aboutMe"
                      hasContent={hasBio}
                      onEdit={openModal('profileDescription')}
                    >
                      <div className={css.bioText}>
                        <div
                          className={css.bioContent}
                          dangerouslySetInnerHTML={{
                            __html:
                              showFullBio || (bio || '').length <= BIO_PREVIEW_LENGTH
                                ? bio
                                : `${getSafeBioSlice(bio, BIO_PREVIEW_LENGTH)}...`,
                          }}
                        />
                        {(bio || '').length > BIO_PREVIEW_LENGTH && (
                          <div
                            className={css.toggleShowMore}
                            onClick={() => setShowFullBio(prev => !prev)}
                          >
                            <FormattedMessage
                              id={
                                showFullBio
                                  ? 'ProfileSettingsPage.showLess'
                                  : 'ProfileSettingsPage.showMore'
                              }
                            />
                          </div>
                        )}
                      </div>
                    </ProfileSettingsTile>

                    {/* Skills */}
                    <ProfileSettingsTile
                      id="experience"
                      titleId="ProfileSettingsPage.skills"
                      hasContent={hasExperience}
                      onEdit={openModal('experience')}
                      scorePill={<ScorePill points={5} />}
                    >
                      <div className={css.skillPills}>
                        {(initialExperienceKeys || []).map((key, i) => {
                          const opt = skillOptions.find(o => o.key === key);
                          return opt ? (
                            <span key={i} className={css.skillPill}>
                              {opt.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </ProfileSettingsTile>

                    {/* Tools */}
                    <ProfileSettingsTile
                      id="tools"
                      titleId="ProfileSettingsPage.tools"
                      hasContent={hasTools}
                      onEdit={openModal('tools')}
                    >
                      <div className={css.toolTags}>
                        {publicData?.tools?.map((tool, i) => {
                          const opt = toolOptions.find(o => o.key === tool.category);
                          return opt ? (
                            <span key={i} className={css.toolContainer}>
                              <ToolLogo logoUrl={opt.logoUrl} className={css.toolLogo} />
                              {opt.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </ProfileSettingsTile>

                    {/* Hobbies */}
                    <ProfileSettingsTile
                      id="hobbies"
                      titleId="ProfileSettingsPage.hobbies"
                      hasContent={hasHobbies}
                      onEdit={openModal('hobbies')}
                    >
                      <div className={css.toolTags}>
                        {publicData?.hobbies?.map((hobby, i) => {
                          const opt = hobbyOptions.find(o => o.key === hobby.category);
                          return opt ? (
                            <span key={i} className={css.toolTag}>
                              {opt.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </ProfileSettingsTile>

                    {/* Portfolio */}
                    <ProfileSettingsTile
                      id="portfolio"
                      titleId="ProfileSettingsPage.portfolio"
                      hasContent={hasPortfolio}
                      onEdit={null}
                      addLink={isPortfolioReorderMode ? null : portfolioAddLink}
                      showAddWhenFilled={true}
                      showPinIcon={true}
                      onPinClick={portfolioWorks.length > 1 ? handleTogglePortfolioReorder : null}
                      isPinActive={isPortfolioReorderMode}
                      scorePill={<ScorePill points={10} perItem />}
                    >
                      {isPortfolioReorderMode ? (
                        <DndContext
                          sensors={dndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handlePortfolioDragEnd}
                        >
                          <SortableContext
                            items={portfolioReorderIds}
                            strategy={rectSortingStrategy}
                          >
                            <div className={`${css.portfolioItems} ${css.portfolioItemsReorder}`}>
                              {portfolioReorderIds.map(id => {
                                const work = portfolioWorksById[id];
                                if (!work) return null;
                                return <SortablePortfolioCard key={id} id={id} work={work} />;
                              })}
                            </div>
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <div className={css.portfolioItems}>
                          {(showAllPortfolio
                            ? sortedPortfolioWorks
                            : sortedPortfolioWorks.slice(0, 3)
                          ).map(work => {
                            const imageUrl = getPortfolioImageUrl(work);
                            const title = work.attributes.title;
                            const published = work?.attributes?.state === 'published';
                            const linkName = published
                              ? 'PortfolioWorkPageCanonical'
                              : 'PortfolioWorkPageDraftPreview';
                            const params = published
                              ? { id: work.id.uuid, slug: createSlug(title) }
                              : {
                                  id: work.id.uuid,
                                  slug: createSlug(title),
                                  type: 'draft',
                                  preview: 'preview',
                                };
                            return (
                              <NamedLink
                                key={work.id.uuid}
                                name={linkName}
                                params={params}
                                className={css.portfolioCard}
                              >
                                {imageUrl ? (
                                  <img src={imageUrl} alt={title} className={css.portfolioImage} />
                                ) : null}
                                {!published ? (
                                  <span className={css.portfolioDraftBadge}>
                                    <FormattedMessage id="ProfileSettingsPage.portfolioDraftBadge" />
                                  </span>
                                ) : null}
                                <div className={css.portfolioOverlay}>
                                  <p className={css.portfolioTitle}>{title}</p>
                                </div>
                              </NamedLink>
                            );
                          })}
                        </div>
                      )}
                      {isPortfolioReorderMode ? (
                        <div className={css.portfolioReorderActions}>
                          <p className={css.portfolioReorderHint}>
                            <FormattedMessage id="ProfileSettingsPage.portfolioReorderHint" />
                          </p>
                          <div className={css.portfolioReorderButtons}>
                            <button
                              type="button"
                              className={css.portfolioCancelButton}
                              onClick={handleCancelPortfolioReorder}
                              disabled={updateInProgress}
                            >
                              <FormattedMessage id="ProfileSettingsPage.portfolioCancelOrder" />
                            </button>
                            <button
                              type="button"
                              className={css.portfolioSaveButton}
                              onClick={handleSavePortfolioReorder}
                              disabled={updateInProgress}
                            >
                              {updateInProgress ? (
                                <IconSpinner className={css.portfolioSaveSpinner} />
                              ) : (
                                <FormattedMessage id="ProfileSettingsPage.portfolioSaveOrder" />
                              )}
                            </button>
                          </div>
                        </div>
                      ) : portfolioWorks.length > 3 ? (
                        <div
                          className={css.toggleShowMore}
                          onClick={() => setShowAllPortfolio(prev => !prev)}
                        >
                          <FormattedMessage
                            id={
                              showAllPortfolio
                                ? 'ProfileSettingsPage.showLess'
                                : 'ProfileSettingsPage.showMore'
                            }
                          />
                        </div>
                      ) : null}
                    </ProfileSettingsTile>

                    {/* Testimonials */}
                    <ProfileSettingsTile
                      id="testimonials"
                      titleId="ProfileSettingsPage.testimonials"
                      hasContent={hasTestimonials}
                      onAdd={() => setActiveModal('requestTestimonial')}
                      showAddWhenFilled={true}
                      scorePill={<ScorePill points={10} perItem />}
                    >
                      <div className={css.listItems}>
                        {offPlatformTestimonials?.map((t, i) => {
                          const boundWorkExperience = (publicData?.workExperience || []).find(
                            w => w.id === t.workExperienceId
                          );
                          const boundWorkedWith = (publicData?.workedWith || []).find(
                            w => w.id === t.workExperienceId
                          );
                          const companyName =
                            boundWorkExperience?.customerName ||
                            boundWorkedWith?.customerName ||
                            t.workExperienceCustomerName ||
                            t.companyName ||
                            t.authorName;
                          const isPublished = t.state === 'published';
                          const key = t._id || t.id || `${t.workExperienceId}-${i}`;
                          const isExpanded = !!expandedTestimonials[key];
                          const toggleExpanded = () =>
                            setExpandedTestimonials(prev => ({ ...prev, [key]: !prev[key] }));
                          return (
                            <div key={key} className={css.listItemColumn}>
                              <button
                                type="button"
                                className={css.listItem}
                                onClick={isPublished ? toggleExpanded : undefined}
                                aria-disabled={!isPublished}
                              >
                                <div className={css.listItemLeft}>
                                  <span className={css.listItemCompany}>{companyName}</span>
                                  {t.verified ? (
                                    <MdVerified className={css.listItemVerified} />
                                  ) : null}
                                  <span className={css.listItemMeta}>{t.authorName}</span>
                                  {!isPublished ? (
                                    <span className={css.listItemPending}>
                                      <FormattedMessage id="ProfileSettingsPage.testimonialPending" />
                                    </span>
                                  ) : null}
                                </div>
                                {isPublished ? (
                                  <IoIosArrowForward
                                    className={`${css.listItemArrow} ${
                                      isExpanded ? css.listItemArrowExpanded : ''
                                    }`}
                                  />
                                ) : null}
                              </button>
                              {isPublished && isExpanded ? (
                                <div className={css.testimonialContent}>
                                  {t.workExperienceTask ? (
                                    <div className={css.testimonialTask}>
                                      {t.workExperienceTask}
                                    </div>
                                  ) : null}
                                  {t.content ? (
                                    <div className={css.testimonialQuote}>"{t.content}"</div>
                                  ) : null}
                                  {t.authorName || t.authorPosition ? (
                                    <div className={css.testimonialAuthor}>
                                      {t.authorName}
                                      {t.authorPosition ? `, ${t.authorPosition}` : ''}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </ProfileSettingsTile>

                    {/* Past Clients */}
                    <ProfileSettingsTile
                      id="workedWith"
                      titleId="ProfileSettingsPage.pastClients"
                      hasContent={hasWorkedWith || hasPrivateWorkedWith}
                      onAdd={openAddPastClient}
                      showAddWhenFilled={true}
                    >
                      <div className={css.listItems}>
                        {publicData?.workedWith?.map((client, i) => {
                          const startDate =
                            client.startMonth && client.startYear
                              ? `${client.startMonth}/${client.startYear}`
                              : '';
                          return (
                            <div key={client.id || i} className={css.listItem}>
                              <div className={css.listItemLeft}>
                                <span className={css.listItemCompany}>{client.customerName}</span>
                                {client.verified ? (
                                  <MdVerified className={css.listItemVerified} />
                                ) : null}
                                <span className={css.listItemMeta}>
                                  {startDate}
                                  {client.jobContinues ? (
                                    <FormattedMessage id="ProfilePage.workExperienceDurationOngoing" />
                                  ) : client.endMonth && client.endYear ? (
                                    ` - ${client.endMonth}/${client.endYear}`
                                  ) : (
                                    ''
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={css.listItemEditPen}
                                onClick={openEditPastClient(client, true)}
                              >
                                <TbPencil className={css.editPenIcon} />
                              </button>
                            </div>
                          );
                        })}
                        {publicData?.privateWorkedWith?.map((client, i) => {
                          const startDate =
                            client.startMonth && client.startYear
                              ? `${client.startMonth}/${client.startYear}`
                              : '';
                          return (
                            <div key={client.id || i} className={css.listItem}>
                              <div className={css.listItemLeft}>
                                <span className={css.listItemCompany}>{client.customerName}</span>
                                <span className={css.listItemPrivate}>
                                  <FormattedMessage id="ProfileSettingsPage.privateBadge" />
                                </span>
                                <span className={css.listItemMeta}>
                                  {startDate}
                                  {client.jobContinues ? (
                                    <FormattedMessage id="ProfilePage.workExperienceDurationOngoing" />
                                  ) : client.endMonth && client.endYear ? (
                                    ` - ${client.endMonth}/${client.endYear}`
                                  ) : (
                                    ''
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={css.listItemEditPen}
                                onClick={openEditPastClient(client, false)}
                              >
                                <TbPencil className={css.editPenIcon} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </ProfileSettingsTile>

                    {/* Work Experience */}
                    <ProfileSettingsTile
                      id="workExperience"
                      titleId="ProfileSettingsPage.workExperience"
                      hasContent={hasWorkExperience || hasPrivateWorkExperience}
                      onAdd={openAddWorkExperience}
                      showAddWhenFilled={true}
                    >
                      <div className={css.listItems}>
                        {publicData?.workExperience?.map((exp, i) => {
                          const startDate =
                            exp.startMonth && exp.startYear
                              ? `${exp.startMonth}/${exp.startYear}`
                              : '';
                          return (
                            <div key={exp.id || i} className={css.listItem}>
                              <div className={css.listItemLeft}>
                                <span className={css.listItemCompany}>{exp.customerName}</span>
                                {exp.verified ? (
                                  <MdVerified className={css.listItemVerified} />
                                ) : null}
                                <span className={css.listItemMeta}>
                                  {startDate}
                                  {exp.jobContinues ? (
                                    <FormattedMessage id="ProfilePage.workExperienceDurationOngoing" />
                                  ) : exp.endMonth && exp.endYear ? (
                                    ` - ${exp.endMonth}/${exp.endYear}`
                                  ) : (
                                    ''
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={css.listItemEditPen}
                                onClick={openEditWorkExperience(exp, true)}
                              >
                                <TbPencil className={css.editPenIcon} />
                              </button>
                            </div>
                          );
                        })}
                        {publicData?.privateWorkExperience?.map((exp, i) => {
                          const startDate =
                            exp.startMonth && exp.startYear
                              ? `${exp.startMonth}/${exp.startYear}`
                              : '';
                          return (
                            <div key={exp.id || i} className={css.listItem}>
                              <div className={css.listItemLeft}>
                                <span className={css.listItemCompany}>{exp.customerName}</span>
                                <span className={css.listItemPrivate}>
                                  <FormattedMessage id="ProfileSettingsPage.privateBadge" />
                                </span>
                                <span className={css.listItemMeta}>
                                  {startDate}
                                  {exp.jobContinues ? (
                                    <FormattedMessage id="ProfilePage.workExperienceDurationOngoing" />
                                  ) : exp.endMonth && exp.endYear ? (
                                    ` - ${exp.endMonth}/${exp.endYear}`
                                  ) : (
                                    ''
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={css.listItemEditPen}
                                onClick={openEditWorkExperience(exp, false)}
                              >
                                <TbPencil className={css.editPenIcon} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </ProfileSettingsTile>

                    {/* Education */}
                    <ProfileSettingsTile
                      id="education"
                      titleId="ProfileSettingsPage.education"
                      hasContent={hasEducation}
                      onAdd={openAddEducation}
                      showAddWhenFilled={true}
                      scorePill={!hasEducation ? <ScorePill points={5} /> : null}
                    >
                      <div className={css.listItems}>
                        {publicData?.education?.map((edu, i) => (
                          <div key={edu.id || i} className={css.listItem}>
                            <div className={css.listItemLeft}>
                              <span className={css.listItemCompany}>{edu.school || edu.where}</span>
                              <span className={css.listItemMeta}>
                                {edu.degree}
                                {edu.graduationYear || edu.when
                                  ? `, ${edu.graduationYear || edu.when}`
                                  : ''}
                              </span>
                            </div>
                            <button
                              type="button"
                              className={css.listItemEditPen}
                              onClick={openEditEducation(edu)}
                            >
                              <TbPencil className={css.editPenIcon} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </ProfileSettingsTile>
                  </div>
                ) : isValidCustomer ? (
                  <div className={css.tilesColumn}>
                    {/* About me */}
                    <ProfileSettingsTile
                      id="bio"
                      titleId="ProfileSettingsPage.aboutMe"
                      hasContent={hasBio}
                      onEdit={openModal('profileDescription')}
                    >
                      <div className={css.bioText}>
                        <div
                          className={css.bioContent}
                          dangerouslySetInnerHTML={{
                            __html:
                              showFullBio || (bio || '').length <= BIO_PREVIEW_LENGTH
                                ? bio
                                : `${getSafeBioSlice(bio, BIO_PREVIEW_LENGTH)}...`,
                          }}
                        />
                        {(bio || '').length > BIO_PREVIEW_LENGTH && (
                          <div
                            className={css.toggleShowMore}
                            onClick={() => setShowFullBio(prev => !prev)}
                          >
                            <FormattedMessage
                              id={
                                showFullBio
                                  ? 'ProfileSettingsPage.showLess'
                                  : 'ProfileSettingsPage.showMore'
                              }
                            />
                          </div>
                        )}
                      </div>
                    </ProfileSettingsTile>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Edit modal */}
            <Modal
              id="ProfileSettingsPage"
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              onManageDisableScrolling={onManageDisableScrolling}
              usePortal={true}
              moreBasis={true}
            >
              <h3>{modalContent}</h3>
            </Modal>

            <RequestWorkExperienceTestimonialForm
              isOpen={activeModal === 'requestTestimonial'}
              onClose={closeRequestTestimonialModal}
              onManageDisableScrolling={onManageDisableScrolling}
              workExperiences={(publicData?.workExperience || []).filter(
                w => !(offPlatformTestimonials || []).some(t => t.workExperienceId === w.id)
              )}
              pastClients={(publicData?.workedWith || []).filter(
                w => !(offPlatformTestimonials || []).some(t => t.workExperienceId === w.id)
              )}
              preselectedItem={testimonialRequest}
              freelancerName={[firstName, lastName].filter(Boolean).join(' ')}
              updateInProgress={updateInProgress}
              onSubmit={({ authorEmail, authorName, workExperienceId }) => {
                onRequestTestimonial(
                  authorEmail,
                  authorName,
                  currentUser.id.uuid,
                  workExperienceId
                );
                onQueryOffPlatformTestimonials(currentUser.id.uuid);
                closeRequestTestimonialModal();
              }}
            />

            {/* New modals */}
            <ProfileSettingsBasicInfoForm
              isOpen={activeModal === 'basicInfo'}
              onClose={closeActiveModal}
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={handlePartialSubmit}
              location={publicData?.location || null}
              languages={(publicData?.languages || []).map(l =>
                typeof l === 'string' ? l : l.language
              )}
              hourlyFee={publicData?.hourlyFee || ''}
              dontShowHourlyFee={publicData?.dontShowHourlyFee}
              languageOptions={languageOptions}
              updateInProgress={updateInProgress}
              hideHourlyFee={isValidCustomer}
            />

            <ProfileSettingsSkillsForm
              isOpen={activeModal === 'experience'}
              onClose={closeActiveModal}
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={handlePartialSubmit}
              onAddTool={handleAddToolFromSkills}
              experienceKeys={initialExperienceKeys}
              skillOptions={skillOptions}
              skillCategoryOptions={skillCategoryOptions || []}
              toolOptions={toolOptions}
              toolKeys={(publicData?.tools || []).map(t => t.category)}
              updateInProgress={updateInProgress}
            />

            <ProfileSettingsToolsForm
              isOpen={activeModal === 'tools'}
              onClose={closeActiveModal}
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={handlePartialSubmit}
              tools={(publicData?.tools || []).map(t => t.category)}
              toolOptions={toolOptions}
              updateInProgress={updateInProgress}
            />

            <ProfileSettingsHobbiesForm
              isOpen={activeModal === 'hobbies'}
              onClose={closeActiveModal}
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={handlePartialSubmit}
              hobbies={(publicData?.hobbies || []).map(h => h.category)}
              hobbyOptions={hobbyOptions}
              updateInProgress={updateInProgress}
            />

            <ProfileSettingsProfileTitleForm
              isOpen={activeModal === 'profileHeader'}
              onClose={() => {
                onClearImage();
                closeActiveModal();
              }}
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={handlePartialSubmit}
              initialValues={{
                firstName: firstName || '',
                lastName: lastName || '',
                whoAmI: publicData?.whoAmI || '',
                imageDirty: !!profileImage?.uploadedImage,
              }}
              profileImagePreview={
                hasProfileImage ? (
                  avatarNode
                ) : (
                  <AvatarLarge user={transientUser} disableProfileLink disableVerifiedBadge />
                )
              }
              onImageUpload={onImageUpload}
              uploadInProgress={uploadInProgress}
              updateInProgress={updateInProgress}
              isCustomer={isValidCustomer}
            />

            <ProfileSettingsProfileDescriptionForm
              isOpen={activeModal === 'profileDescription'}
              onClose={closeActiveModal}
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={handlePartialSubmit}
              initialValue={bio || ''}
              updateInProgress={updateInProgress}
              isCustomer={isValidCustomer}
            />

            <ProfileSettingsPastClientForm
              isOpen={activeModal === 'pastClient'}
              onClose={closePastClientModal}
              onManageDisableScrolling={onManageDisableScrolling}
              editingItem={editingPastClient?.item || null}
              initialIsPublic={editingPastClient?.isPublic ?? true}
              onSubmit={handlePastClientSubmit}
              onDelete={handlePastClientDelete}
              testimonialState={
                editingPastClient?.item
                  ? (offPlatformTestimonials || []).find(
                      t => t.workExperienceId === editingPastClient.item.id
                    )?.state ?? null
                  : null
              }
              onOpenRequestTestimonial={handleOpenRequestTestimonialFromPastClient}
              updateInProgress={updateInProgress}
            />

            <ProfileSettingsWorkExperienceForm
              isOpen={activeModal === 'workExperience'}
              onClose={closeWorkExperienceModal}
              onManageDisableScrolling={onManageDisableScrolling}
              editingItem={editingWorkExperience?.item || null}
              initialIsPublic={editingWorkExperience?.isPublic ?? true}
              onSubmit={handleWorkExperienceSubmit}
              onDelete={handleWorkExperienceDelete}
              testimonialState={
                editingWorkExperience?.item
                  ? (offPlatformTestimonials || []).find(
                      t => t.workExperienceId === editingWorkExperience.item.id
                    )?.state ?? null
                  : null
              }
              onOpenRequestTestimonial={handleOpenRequestTestimonialFromWorkExperience}
              updateInProgress={updateInProgress}
            />

            <ProfileSettingsModal
              id="codeOfConduct"
              isOpen={activeModal === 'codeOfConduct'}
              onClose={closeActiveModal}
              onManageDisableScrolling={onManageDisableScrolling}
              title={intl.formatMessage({ id: 'ProfileSettingsPage.codeOfConduct' })}
              onSubmit={() => {
                if (!cocAccepted) {
                  handlePartialSubmit({ cocAccepted: true });
                }
                closeActiveModal();
              }}
              submitDisabled={cocAccepted}
              submitInProgress={updateInProgress}
              submitLabel={intl.formatMessage({
                id: cocAccepted
                  ? 'ProfileSettingsPage.codeOfConductAcceptedButton'
                  : 'ProfileSettingsPage.codeOfConductAcceptButton',
              })}
            >
              <CodeOfConductModalContent />
            </ProfileSettingsModal>

            <ProfileSettingsEducationForm
              isOpen={activeModal === 'education'}
              onClose={closeEducationModal}
              onManageDisableScrolling={onManageDisableScrolling}
              editingItem={editingEducation?.item || null}
              onSubmit={handleEducationSubmit}
              onDelete={handleEducationDelete}
              updateInProgress={updateInProgress}
            />
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ProfileSettingsPageComponent.defaultProps = {
  currentUser: null,
  uploadImageError: null,
  updateProfileError: null,
  image: null,
};

const { bool, func, object, shape, string } = PropTypes;

ProfileSettingsPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  image: shape({
    id: string,
    imageId: propTypes.uuid,
    file: object,
    uploadedImage: propTypes.image,
  }),
  onImageUpload: func.isRequired,
  onClearImage: func.isRequired,
  onUpdateProfile: func.isRequired,
  scrollingDisabled: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, currentUserFetchProgress, currentUserFetchSuccess } = state.user;
  const {
    image,
    uploadImageError,
    uploadInProgress,
    updateInProgress,
    updateProfileError,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    userListingRefs,
  } = state.ProfileSettingsPage;
  const { skillOptions, toolOptions, skillCategoryOptions } = state.SkillsAndTools;
  const listings = getMarketplaceEntities(state, userListingRefs);

  return {
    currentUser,
    currentUserFetchProgress,
    currentUserFetchSuccess,
    image,
    scrollingDisabled: isScrollingDisabled(state),
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    skillOptions,
    toolOptions,
    skillCategoryOptions,
    listings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onImageUpload: data => dispatch(uploadImage(data)),
  onClearImage: () => dispatch(clearImage()),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onSetInitialState: () => dispatch(setInitialState()),
  onRequestTestimonial: (authorEmail, authorName, freelancerId, workExperienceId) =>
    dispatch(requestTestimonial(authorEmail, authorName, freelancerId, workExperienceId)),
  onQueryOffPlatformTestimonials: userId => dispatch(queryOffPlatformTestimonials(userId)),
});

const ProfileSettingsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(ProfileSettingsPageComponent);

ProfileSettingsPage.loadData = loadData;

export default ProfileSettingsPage;
